import { Injectable } from '@angular/core';
import { AppRideStatus, WayPoint } from '@caballus/common';
import {
    CapacitorPluginService,
    NetworkStatusService,
    Ride,
    RideService,
    RideStatus
} from '@caballus/ui-common';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, share, switchMap, tap } from 'rxjs/operators';
import { StorageService } from '../../services/storage.service';
import { HorseCache } from '../horse/horse.cache';
import { MediaCache } from '../media/media.cache';

enum Keys {
    UnsyncedRides = 'unsynced-rides-',
    CurrentRide = 'current-ride-',
    UploadingMedias = 'uploading-medias-',
    CurrentRideWaypointsToSync = 'current-ride-waypoints-to-sync-'
}

export { Keys as RideCacheKeys };

@Injectable({ providedIn: 'root' })
export class RideCache {
    private _currentUnsyncedChangesUpload$: Observable<void> = null;
    private _wayPointChangesRideId: string = null;

    constructor(
        private readonly _storageService: StorageService,
        private readonly _capacitorPluginService: CapacitorPluginService,
        private readonly _rideService: RideService,
        private readonly _horseCache: HorseCache,
        private readonly _mediaCache: MediaCache,
        private readonly _networkStatusService: NetworkStatusService
    ) {}

    public sync(): Observable<void> {
        return this._networkStatusService.networkStatus$.pipe(
            switchMap(network => {
                if (!network.isOnline) {
                    return of(undefined);
                } else {
                    return this._uploadUnsyncedChanges().pipe(
                        switchMap(() => {
                            return of(null);
                        }),
                        // Handle errors if any of the observables fail
                        catchError(error => {
                            return of(undefined);
                        })
                    );
                }
            })
        );
    }

    private _uploadUnsyncedChangesIfConnected(): Observable<void> {
        return this._networkStatusService.networkStatus$.pipe(
            switchMap(status => (status.isOnline ? this._uploadUnsyncedChanges() : of(undefined)))
        );
    }

    private _uploadUnsyncedChanges(): Observable<void> {
        return this._cachedUnsyncedRides().pipe(
            switchMap(unsynced => {
                if (!unsynced.length) {
                    return of(undefined);
                }

                if (!this._currentUnsyncedChangesUpload$) {
                    this._currentUnsyncedChangesUpload$ = this._uploadUnsyncedRides().pipe(
                        switchMap(successfulUploads => {
                            if (successfulUploads?.length) {
                                // Only remove successfully uploaded rides from storage
                                return this._removeSuccessfulUploadsFromStorage(successfulUploads);
                            }
                            return of(undefined);
                        }),
                        tap(() => (this._currentUnsyncedChangesUpload$ = null)),
                        share()
                    );
                }

                return this._currentUnsyncedChangesUpload$;
            }),
            catchError(error => {
                return of(undefined);
            })
        );
    }

    private _parseWaypoints(json: string): WayPoint[] {
        return Array.isArray(json) ? json.map(e => new WayPoint(e)) : [];
    }

    public saveRideDetails(rideWithDetails: Ride): Observable<void> {
        if (rideWithDetails.appRideStatus !== AppRideStatus.Finalized) {
            throw new Error('Ride must be finalized to save');
        }

        // Migrate current ride media to upload queue
        this.migrateCurrentRideMediaToUploadQueue().subscribe();

        // Add finalized ride to horse cache
        return this._horseCache.addFinalizedRide(rideWithDetails).pipe(
            switchMap(() => {
                if (this._networkStatusService.currentNetworkStatus.isOnline) {
                    // Upload here only — do not also enqueue, or the sync queue sends a duplicate PATCH
                    // (and duplicate RideEnded push notifications).
                    return this._directUploadFinalizedRideDetails(rideWithDetails);
                }
                return this._enqueueRideDetailsForSync(rideWithDetails);
            }),
            catchError(error => {
                console.error('Error adding finalized ride to cache', error);

                // If any step fails, ensure the ride is in the upload queue
                return this._addRideToUploadQueue(rideWithDetails);
            })
        );
    }

    /**
     * Saves finalized ride details to the API immediately; still drains any other pending uploads.
     */
    private _directUploadFinalizedRideDetails(ride: Ride): Observable<void> {
        return this._rideService.saveRideDetails(ride).pipe(
            switchMap(() => from(this._storageService.clearCurrentRideData())),
            switchMap(() => this._uploadUnsyncedChangesIfConnected()),
            catchError(() => this._enqueueRideDetailsForSync(ride))
        );
    }

    private _enqueueRideDetailsForSync(ride: Ride): Observable<void> {
        return this._addRideToUploadQueue(ride).pipe(
            switchMap(() => this._uploadUnsyncedChangesIfConnected())
        );
    }

    public migrateCurrentRideMediaToUploadQueue(): Observable<void> {
        return this._mediaCache.migrateCurrentRideMediaToUploadQueue();
    }

    private _addRideToUploadQueue(ride: Ride): Observable<void> {
        return this._cachedUnsyncedRides().pipe(
            map(unsynced => [...unsynced, ride]),
            switchMap(allUnsynced =>
                from(
                    this._storageService.setUserData(
                        Keys.UnsyncedRides,
                        JSON.stringify(allUnsynced)
                    )
                )
            )
        );
    }

    private _uploadUnsyncedRides(): Observable<string[]> {
        return this._cachedUnsyncedRides().pipe(
            switchMap(rides => {
                const uploadObservables = rides.map(ride =>
                    this._uploadSingleRide(ride).pipe(
                        map(() => ride._id),
                        catchError(error => {
                            return of(null);
                        })
                    )
                );

                return forkJoin(uploadObservables).pipe(
                    map(results => results.filter(id => id !== null))
                );
            })
        );
    }

    private _uploadSingleRide(ride: Ride): Observable<void> {
        if (!ride.rideStatus) {
            return this._handleNewRideUpload(ride);
        } else if (ride.rideStatus === RideStatus.InProgress) {
            return this._handleInProgressRideUpload(ride);
        }
        return this._rideService.saveRideDetails(ride);
    }

    private _handleNewRideUpload(ride: Ride): Observable<void> {
        return this._rideService
            .startRide({
                _id: ride._id,
                startDateTime: ride.startDateTime,
                horseIds: ride.horseIdentities.map(h => h._id)
            })
            .pipe(
                switchMap(() =>
                    this._rideService.endRide({
                        _id: ride._id,
                        startDateTime: ride.startDateTime,
                        endDateTime: ride.endDateTime,
                        horseIds: ride.horseIdentities.map(h => h._id),
                        paths: ride.paths,
                        pauseTimeInSeconds: ride.pauseTimeInSeconds,
                        distanceKilometers: ride.distanceKilometers
                    })
                ),
                switchMap(() => this._rideService.saveRideDetails(ride))
            );
    }

    private _handleInProgressRideUpload(ride: Ride): Observable<void> {
        return this._rideService
            .endRide({
                _id: ride._id,
                startDateTime: ride.startDateTime,
                endDateTime: ride.endDateTime,
                horseIds: ride.horseIdentities.map(h => h._id),
                paths: ride.paths,
                pauseTimeInSeconds: ride.pauseTimeInSeconds,
                distanceKilometers: ride.distanceKilometers
            })
            .pipe(switchMap(() => this._rideService.saveRideDetails(ride)));
    }

    private _removeSuccessfulUploadsFromStorage(successfulIds: string[]): Observable<void> {
        return this._cachedUnsyncedRides().pipe(
            map(rides => rides.filter(ride => !successfulIds.includes(ride._id))),
            switchMap(remainingRides =>
                from(
                    this._storageService.setUserData(
                        Keys.UnsyncedRides,
                        JSON.stringify(remainingRides)
                    )
                )
            )
        );
    }

    private _parseRides(json: Ride[]): Ride[] {
        return Array.isArray(json) ? json.map(e => new Ride(e)) : [];
    }

    private _cachedUnsyncedRides(): Observable<Ride[]> {
        return from(this._storageService.getUserData<Ride[]>(Keys.UnsyncedRides, false)).pipe(
            map(json => this._parseRides(json)),
            catchError(e => {
                return of(e);
            })
        );
    }
}
